import { useForm } from "@inertiajs/react";
import { FormEvent, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";

const RESEND_COOLDOWN_SECONDS = 60;

type Props = {
  errors?: Record<string, string | string[]>;
  onBack: () => void;
};

// Two-phase Slack OTP login: submit an email, then the 6-digit code DM'd to
// that email's Slack account. Server-side failures come back as the
// slack_email / slack_code error keys.
export default function SlackLoginPanel({ errors, onBack }: Props) {
  const [phase, setPhase] = useState<"email" | "code">("email");
  const [cooldown, setCooldown] = useState(0);
  const { data, setData, post, processing } = useForm({ email: "", code: "" });

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const errorFor = (key: string) => {
    const value = errors?.[key];
    return Array.isArray(value) ? value.join(", ") : value;
  };

  const requestCode = (e?: FormEvent) => {
    e?.preventDefault();
    post("/slack_login", {
      onSuccess: () => {
        setPhase("code");
        setCooldown(RESEND_COOLDOWN_SECONDS);
      },
    });
  };

  const verifyCode = (e: FormEvent) => {
    e.preventDefault();
    post("/slack_login/verify");
  };

  if (phase === "email") {
    return (
      <form onSubmit={requestCode} className="space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="slack-email" className="text-sm font-medium text-foreground">Email</label>
          <Input
            id="slack-email"
            type="email"
            value={data.email}
            onChange={(e) => setData("email", e.target.value)}
            autoComplete="email"
            autoFocus
            required
          />
          <p className="text-xs text-muted-foreground">
            We'll DM a one-time code to your Slack account with this email.
          </p>
          {errorFor("slack_email") && (
            <p className="text-sm text-destructive">{errorFor("slack_email")}</p>
          )}
        </div>

        <Button type="submit" className="w-full" disabled={processing}>
          {processing ? "Sending code…" : "Send code to Slack"}
        </Button>

        <button
          type="button"
          onClick={onBack}
          className="w-full text-center text-sm text-link hover:underline cursor-pointer"
        >
          Back to password sign-in
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={verifyCode} className="space-y-4">
      <div className="space-y-1.5">
        <label htmlFor="slack-code" className="text-sm font-medium text-foreground">6-digit code</label>
        <Input
          id="slack-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={data.code}
          onChange={(e) => setData("code", e.target.value.replace(/\D/g, ""))}
          autoFocus
          required
          className="text-center tracking-[0.5em]"
        />
        <p className="text-xs text-muted-foreground">
          Check your Slack DMs — we sent a code to {data.email}. It expires in 5 minutes.
        </p>
        {errorFor("slack_code") && (
          <p className="text-sm text-destructive">{errorFor("slack_code")}</p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={processing || data.code.length !== 6}>
        {processing ? "Verifying…" : "Verify and sign in"}
      </Button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={() => {
            setPhase("email");
            setData("code", "");
          }}
          className="text-link hover:underline cursor-pointer"
        >
          Use a different email
        </button>
        <button
          type="button"
          onClick={() => requestCode()}
          disabled={cooldown > 0 || processing}
          className="text-link hover:underline disabled:text-muted-foreground disabled:no-underline cursor-pointer disabled:cursor-default"
        >
          {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
        </button>
      </div>
    </form>
  );
}
